import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LanguageContext, type LanguageContextValue } from './LanguageContext';
import { createTranslator, type Language } from './messages';

const STORAGE_KEY = 'backoffice_language';

function isStorageError(error: unknown): error is DOMException {
  return (
    error instanceof DOMException &&
    (error.name === 'SecurityError' || error.name === 'QuotaExceededError')
  );
}

function initialPreference(): { language: Language; preferenceUnavailable: boolean } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ja') {
      return { language: stored, preferenceUnavailable: false };
    }
    if (stored !== null) console.warn('Unsupported stored language preference:', stored);
    return { language: 'en', preferenceUnavailable: false };
  } catch (error) {
    if (!isStorageError(error)) throw error;
    console.warn('Unable to read language preference:', error);
    return { language: 'en', preferenceUnavailable: true };
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState(initialPreference);

  const setLanguage = useCallback((language: Language) => {
    let preferenceUnavailable = false;
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (error) {
      if (!isStorageError(error)) throw error;
      console.warn('Unable to save language preference:', error);
      preferenceUnavailable = true;
    }
    setPreference({ language, preferenceUnavailable });
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({
      ...createTranslator(preference.language),
      ...preference,
      locale: preference.language === 'ja' ? 'ja-JP' : 'en-US',
      setLanguage,
    }),
    [preference, setLanguage],
  );

  useEffect(() => {
    document.documentElement.lang = value.language;
    document.title = value.t('Paint Defect Inspection Platform');
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description)
      description.content = value.t('Quality analytics and defect reporting portal.');
  }, [value]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
